import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Accordion, AccordionDetails, AccordionSummary, Button, Divider, Stack, Typography } from '@mui/material'
import { useTableStyles } from '../../shared/styles/table.styles'
import { J1DownarrowD } from '@e3dc-react/icons'
import { TariffModel } from '../models/tariffs-model'
import { TariffsTableMenuIcon } from './TariffsTableMenuIcon'
import { useHagerBridgePermissions } from '../../shared/hooks/use-hager-bridge-permissions'
import { getCurrencyValue } from '../../shared/utils/currency-value'
import { TariffsStatusChip } from '../../shared/components/TariffsStatusChip'
import { TariffCategoryCell } from './TariffCategoryCell'
import { TariffCategory } from '../../shared/models/types'
import useModals from 'src/Hooks/useModals'
import { getCurrentDynamicPrices } from '../utils/get-current-dynamic-prices'
import { useTypedSelector } from 'src/State/RootReducer'
import { format } from '@e3dc-react/shell/Libs/date'

interface TariffsMobileTableRowItemProps {
  tariff: TariffModel
  installationId: number
  index: number
  dynamicTariffActivated: boolean
}

/**
 * Component is visible on mobile and tablet screen/view.
 * It is single row inside tariffs table for smaller screens
 * @param tariff - TariffModel object
 * @param installationId - unique installation identifier as string
 */
export const TariffsMobileTableRowItem: React.FC<TariffsMobileTableRowItemProps> = ({ tariff, installationId, index, dynamicTariffActivated }) => {
  const { t } = useCustomTranslation()
  const { classes } = useTableStyles()
  const { openTariffDetailsDialog } = useModals()
  const isTourRunning = useTypedSelector(state => state.eMobility.tour.isRunning)

  const {
    permissions: { tariffPermissions },
  } = useHagerBridgePermissions()

  const hasTariffMutationPermissions = tariffPermissions.editTariff || tariffPermissions.deleteTariff
  const dynamicPrice = getCurrentDynamicPrices(tariff.dynamicPrices)?.gridPrice

  let gridPriceToDisplay = '-'

  if (tariff.tariffCategory === TariffCategory.staticTariff) {
    gridPriceToDisplay = getCurrencyValue(tariff.staticPrices.gridPrice, tariff.currencyCode)
  }

  if (tariff.tariffCategory === TariffCategory.dynamicTariff && dynamicPrice) {
    gridPriceToDisplay = getCurrencyValue(dynamicPrice, tariff.currencyCode)
  }

  return (
    <Accordion elevation={0} className={classes.accordion} defaultExpanded={isTourRunning && index === 0}>
      <AccordionSummary className={classes.accordionSummary} expandIcon={<J1DownarrowD />}>
        <Stack flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%', ml: 1 }}>
          <Typography>{tariff.tariffName}</Typography>
          {hasTariffMutationPermissions && (
            <TariffsTableMenuIcon tariff={tariff} installationId={installationId} dynamicTariffActivated={dynamicTariffActivated} />
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.tariffs.price')}</Typography>
          <Typography>{gridPriceToDisplay}</Typography>
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.tariffs.status')}</Typography>
          <TariffsStatusChip status={tariff.tariffStatus} />
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.tariffs.startDate')}</Typography>
          <Typography>{tariff.startTime ? format(new Date(tariff.startTime), t('common.format.date-fns-date')) : '-'}</Typography>
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.tariffs.endDate')}</Typography>
          <Typography>{tariff.endTime ? format(new Date(tariff.endTime), t('common.format.date-fns-date')) : '-'}</Typography>
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.tariffs.changed')}</Typography>
          <Typography>{format(new Date(tariff.lastUpdated), t('common.format.date-fns-date'))}</Typography>
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.tariffs.tariff-category')}</Typography>
          <TariffCategoryCell category={tariff.tariffCategory} />
        </Stack>
        <Divider className={classes.divider} />
        <Stack flexDirection={'row'} pt={2}>
          <Button
            id="tariff-details-button"
            variant="text"
            size="medium"
            onClick={() => openTariffDetailsDialog({ installationId, tariffId: tariff.tariffId })}
          >
            {t('emobility.tariffs.tariff-details')}
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
